/*
    * Main Frontend Script *
*/
import { 
    checkAccountCreationFields, 
    checkValidEmail, 
    checkValidPassword, 
    EmailVerificationError, 
    getPrettyErrorMessage,
    RequestError, 
    UpdateProfileError
} from "../common";


//* MARK: Profile box and authentication
// ----------------------------------------------------------------------------

// Profile box dropdown toggle
const profileZone = document.querySelector('.profile-zone') as HTMLElement;
const profileBox = document.getElementById('profile-box');
if (profileBox) {
    profileBox.addEventListener('click', function(e) {
        e.stopPropagation();
        profileBox.classList.toggle('active');
    });

    // Close dropdown when clicking outside
    document.addEventListener('click', function() {
        profileBox.classList.remove('active');
    });
}

// Fetch current user info and show/hide profile box or auth buttons
const authButtons = document.getElementById('auth-buttons');
const nameTag = document.querySelector('#profile-box .display-name') as HTMLSpanElement;

if (profileBox && authButtons) {
    fetch('/api/current-user-info', { method: 'GET' })
        .then(response => response.json())
        .then(data => {
            if (data.isAuthenticated && data.user) {
                // User is connected - show profile box
                profileBox.style.display = 'flex';
                authButtons.style.display = 'none';
                if (nameTag) {
                    nameTag.textContent = data.user.name || 'Utilisateur';
                }
                if (data.user.isAdmin) {
                    const adminLink = profileBox.querySelector('.admin-link') as HTMLElement;
                    if (adminLink) adminLink.style.display = 'block';
                }
            } else {
                // User is not connected - show auth buttons
                profileBox.style.display = 'none';
                authButtons.style.display = 'flex';
            }
            profileZone.style.visibility = 'visible';
        })
        .catch(error => {
            console.error('Error fetching user info:', error);
            // On error, show auth buttons by default
            profileBox.style.display = 'none';
            authButtons.style.display = 'flex';
        });
}


//* MARK: Mobile navigation
// ----------------------------------------------------------------------------

// On-off navigation menu for mobile
document.getElementById("nav-burger-icon")?.addEventListener('click', function() {
    const navElement = document.querySelector("nav")!;
    navElement.classList.toggle("opened");
    const isOpened = navElement.classList.contains("opened");
    if (isOpened) {
        history.pushState({ navOpened: true }, '', window.location.href);
    }
});

// Listen for back/forward navigation
window.addEventListener('popstate', function () {
    const navElement = document.querySelector("nav")!;
    if (navElement.classList.contains("opened")) {
        navElement.classList.remove("opened");
    }
});



//* MARK: Reviews carousel
// ----------------------------------------------------------------------------

initReviewsCarousel();

// Reviews carousel navigation
function initReviewsCarousel() {
    const carousel = document.querySelector(".reviews-carousel") as HTMLElement;
    const carouselContent = document.querySelector(".carousel-content") as HTMLElement;
    const leftButton = document.getElementById("reviews-nav-left") as HTMLButtonElement;
    const rightButton = document.getElementById("reviews-nav-right") as HTMLButtonElement;
    const reviewItem = document.querySelector(".reviews-carousel .review") as HTMLElement;
    
    if (!carousel || !carouselContent || !leftButton || !rightButton) {
        return;
    }

    let isButtonScrolling = false; // Flag to track button-initiated scrolls

    function updateButtonStates() {
        if (!carousel || !leftButton || !rightButton) return;
        const scrollLeft = carousel.scrollLeft;
        const maxScroll = carousel.scrollWidth - carousel.clientWidth;
        leftButton.disabled = scrollLeft <= 0;
        rightButton.disabled = scrollLeft >= maxScroll - 1; // -1 for rounding issues
    }

    function smoothScrollToPosition(targetScroll: number) {
        if (!carousel) return;
        
        isButtonScrolling = true;
        const startScroll = carousel.scrollLeft;
        const distance = targetScroll - startScroll;
        const duration = 400; // 400ms for smooth animation
        const startTime = performance.now();

        function animateScroll(currentTime: number) {
            const elapsedTime = currentTime - startTime;
            const progress = Math.min(elapsedTime / duration, 1);
            const easeOut = 1 - Math.pow(1 - progress, 3);
            const currentScroll = startScroll + (distance * easeOut);
            carousel.scrollLeft = currentScroll;
            
            if (progress < 1) {
                requestAnimationFrame(animateScroll);
            } else {
                isButtonScrolling = false;
                updateButtonStates();
            }
        }
        
        requestAnimationFrame(animateScroll);
    }

    function scrollCarousel(direction: 'left' | 'right') {
        if (!carousel || isButtonScrolling) return;
        const currentScroll = carousel.scrollLeft;
        const scrollDistance = carousel.clientWidth + 21; // 3 blocks at a time
        let targetScroll = direction === 'left' 
            ? Math.max(0, currentScroll - scrollDistance)
            : Math.min(carousel.scrollWidth - carousel.clientWidth, currentScroll + scrollDistance);
        const stepSize = reviewItem.clientWidth + (carousel.clientWidth - reviewItem.clientWidth * 3) / 2;
        targetScroll = Math.round(targetScroll / stepSize) * stepSize;
        smoothScrollToPosition(targetScroll);
    }

    // Event listeners and first update call
    leftButton.addEventListener('click', () => scrollCarousel('left'));
    rightButton.addEventListener('click', () => scrollCarousel('right'));
    carousel.addEventListener('scroll', updateButtonStates);
    window.addEventListener('resize', updateButtonStates);
    updateButtonStates();
}


//* MARK: Newsletter form
// ----------------------------------------------------------------------------

const newsletterForm = document.getElementById('newsletter-form') as HTMLFormElement | null;
if (newsletterForm) initNewsletterForm(newsletterForm);

function initNewsletterForm(newsletterForm: HTMLFormElement) {
    const emailInput = newsletterForm.querySelector('input[type="email"]') as HTMLInputElement;
    const submitButton = newsletterForm.querySelector('button[type="submit"]') as HTMLButtonElement;
    const container = newsletterForm.closest('.newsletter-form') as HTMLElement;
    const endProcessButton = document.getElementById('newsletter-end-process') as HTMLElement;
    const confirmElement = container?.querySelector('.sub-confirm') as HTMLElement;
    const errorElement = newsletterForm.querySelector('.error-message') as HTMLElement;

    if (emailInput) emailInput.value = "";

    newsletterForm.addEventListener('submit', async (event: Event) => {
        event.preventDefault();
        if (!emailInput || !submitButton) return;

        const email = emailInput.value.trim() || "";
        if (!checkValidEmail(email)) {
            showError("L'adresse e-mail n'est pas valide");
            return;
        }
        submitButton.disabled = true;
        const isUnsubscribe = newsletterForm.dataset.unsubscribe === 'true';
        const actionUrl = isUnsubscribe ? '/api/newsletter/unsubscribe' : '/api/newsletter/subscribe';
        try {
            const result = await fetch(actionUrl, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email })
            });

            if (result.ok) {
                showSuccess();
            } else {
                const errorData = await result.json();
                throw new Error(errorData.error || 'Unknown error from server');
            }
        } catch (error) {
            console.error('Newsletter subscription error:', error);
            showError("Une erreur est survenue. Veuillez réessayer plus tard.");
            submitButton.disabled = false;
        }
    });

    if (endProcessButton && container && confirmElement) {
        endProcessButton.addEventListener('click', (e) => {
            e.preventDefault();
            newsletterForm.style.display = 'flex';
            confirmElement.style.display = 'none';
            if (emailInput) emailInput.value = "";
            submitButton.disabled = false;
        });
    }

    function showSuccess() {
        if (confirmElement) confirmElement.style.display = 'block';
        newsletterForm.style.display = 'none';
    }

    function showError(message: string) {
        if (errorElement) {
            errorElement.textContent = message;
            errorElement.style.display = 'block';
            setTimeout(() => {
                errorElement.style.display = 'none';
            }, 5000);
        }
    }
}


//* MARK: Signup form
// ----------------------------------------------------------------------------

const signupForm = document.getElementById('signup-form') as HTMLFormElement | null;
if (signupForm) initSignupForm(signupForm);

function initSignupForm(signupForm: HTMLFormElement) {
    const nameInput = signupForm.querySelector('input[name="name"]') as HTMLInputElement;
    const emailInput = signupForm.querySelector('input[type="email"]') as HTMLInputElement;
    const passwordInput = signupForm.querySelector('input[type="password"]') as HTMLInputElement;
    const submitButton = signupForm.querySelector('button[type="submit"]') as HTMLButtonElement;
    const newsletterCheckbox = signupForm.querySelector('input[name="newsletter"]') as HTMLInputElement;
    const container = signupForm.closest('.center-form-container') as HTMLElement;
    const confirmElement = container?.querySelector('.form-success') as HTMLElement;
    const errorElement = signupForm.querySelector('.error-message') as HTMLElement;
    const signupContainer = document.getElementById('signup-form-container') as HTMLDivElement;

    if (!nameInput || !emailInput || !passwordInput || !submitButton || !confirmElement || !errorElement) {
        console.error("Could not initialize signup form: missing elements");
        return;
    }

    nameInput.value = "";
    emailInput.value = "";
    passwordInput.value = "";

    signupForm.addEventListener('submit', async (event: Event) => {
        event.preventDefault();
        submitButton.disabled = true;
        
        const name = nameInput.value.trim() || "";
        const email = emailInput.value.trim() || "";
        const password = passwordInput.value || "";
        const subscribeNewsletter = newsletterCheckbox?.checked || false;

        const fieldsCheck = checkAccountCreationFields(email, name, password);
        if (!fieldsCheck.valid) {
            showError(fieldsCheck.error || 'internal_error');
            return;
        }
        
        try {
            let signupApiEndPoint = '/api/account/signup';
            const urlParams = new URLSearchParams(window.location.search);
            const product = urlParams.get('product');
            if (product === 'standard' || product === 'premium') {
                signupApiEndPoint += `?product=${product}`;
            }
            const result = await fetch(signupApiEndPoint, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ name, email, password, subscribeNewsletter })
            });
            if (result.ok) {
                showSuccess();
            } else {
                const errorData = await result.json();
                console.error('Signup error from server:', errorData);
                showError(errorData.error || 'internal_error');
            }
        } catch (error) {
            console.error('Signup error:', error);
            showError('internal_error');
        } finally {
            submitButton.disabled = false;
        }
    });

    function showSuccess() {
        if (confirmElement) confirmElement.style.display = 'block';
        signupContainer.style.display = 'none';
    }
    
    function showError(requestError: RequestError) {
        if (errorElement) {
            errorElement.textContent = getPrettyErrorMessage(requestError);
            errorElement.style.display = 'block';
            submitButton.disabled = false;
        }
    }
}

//* MARK: Sign-in form
// ----------------------------------------------------------------------------

const signinForm = document.getElementById('signin-form') as HTMLFormElement | null;
if (signinForm) initSigninForm(signinForm);

function initSigninForm(signinForm: HTMLFormElement) {
    const emailInput = signinForm.querySelector('input[type="email"]') as HTMLInputElement;
    const passwordInput = signinForm.querySelector('input[type="password"]') as HTMLInputElement;
    const submitButton = signinForm.querySelector('button[type="submit"]') as HTMLButtonElement;
    const errorElement = signinForm.querySelector('.error-message') as HTMLElement;
    const container = signinForm.closest('.center-form-container') as HTMLElement;
    const confirmElement = container?.querySelector('.form-success') as HTMLElement;
    const formLogo = document.querySelector('.form-logo') as HTMLElement;
    const signinContainer = document.getElementById('signin-form-container') as HTMLDivElement;

    if (!emailInput || !passwordInput || !submitButton || !errorElement || !confirmElement) {
        console.error("Could not initialize signin form: missing elements");
        return;
    }

    emailInput.value = "";
    passwordInput.value = "";

    signinForm.addEventListener('submit', async (event: Event) => {
        event.preventDefault();
        submitButton.disabled = true;
        errorElement.style.display = 'none';
        
        const email = emailInput.value.trim() || "";
        if (!checkValidEmail(email)) {
            showError('no_email_provided');
            return;
        }
        const password = passwordInput.value || "";
        if (password.length < 1) {
            showError('no_password_provided');
        }

        try {
            const result = await fetch('/api/account/signin', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email, password })
            });
            
            if (result.ok) {
                showSuccess();
                // Redirect to profile page after a short delay
                setTimeout(() => {
                    const urlSearchParams = new URLSearchParams(window.location.search);
                    const product = urlSearchParams.get('product');
                    const next = urlSearchParams.get('next');
                    if (product === 'standard' || product === 'premium') {
                        window.location.href = `/purchase?product=${product}`;
                    } else if (next) {
                        window.location.href = next;
                    } else {
                        window.location.href = '/';
                    }
                }, 800);
            } else {
                const errorData = await result.json();
                showError(errorData.error || 'internal_error');
                submitButton.disabled = false;
            }
        } catch (error) {
            console.error('Signin error:', error);
            showError('internal_error');
            submitButton.disabled = false;
        }
    });

    function showSuccess() {
        if (confirmElement) confirmElement.style.display = 'block';
        signinForm.style.display = 'none';
        if (formLogo) formLogo.style.display = 'none';
        if (signinContainer) signinContainer.style.display = 'none';
    }

    function showError(requestError: RequestError) {
        if (errorElement) {
            errorElement.textContent = getPrettyErrorMessage(requestError);
            errorElement.style.display = 'block';
        }
    }
}

//* MARK: Signin/signup purchase process
// ----------------------------------------------------------------------------

if (signupForm || signinForm) {
    const urlParams = new URLSearchParams(window.location.search);
    const product = urlParams.get('product');
    if (product === 'standard' || product === 'premium') {
        initFormPurchaseMode(product);
    } else {
        initFormPurchaseMode('none');
    }
}

function initFormPurchaseMode(mode: 'none' | 'standard' | 'premium') {
    document.getElementById('default-form-logo')?.classList.toggle('hidden', mode !== 'none');
    document.getElementById('purchase-logo-standard')?.classList.toggle('hidden', mode !== 'standard');
    document.getElementById('purchase-logo-premium')?.classList.toggle('hidden', mode !== 'premium');
    document.getElementById('purchase-title-suffix')?.classList.toggle('hidden', mode === 'none');
    document.getElementById('purchase-name-fallback')?.classList.toggle('hidden', mode !== 'none');
    document.getElementById('purchase-name-standard')?.classList.toggle('hidden', mode !== 'standard');
    document.getElementById('purchase-name-premium')?.classList.toggle('hidden', mode !== 'premium');
    document.getElementById('purchase-process-message')?.classList.toggle('hidden', mode === 'none');
}

//* MARK: Forgot password form
// ----------------------------------------------------------------------------

const forgotPasswordForm = document.getElementById('forgot-password-form') as HTMLFormElement | null;
if (forgotPasswordForm) initForgotPasswordForm(forgotPasswordForm);

function initForgotPasswordForm(forgotPasswordForm: HTMLFormElement) {
    const emailInput = forgotPasswordForm.querySelector('input[type="email"]') as HTMLInputElement;
    const submitButton = forgotPasswordForm.querySelector('button[type="submit"]') as HTMLButtonElement;
    const errorElement = forgotPasswordForm.querySelector('.error-message') as HTMLElement;
    const container = forgotPasswordForm.closest('.center-form-container') as HTMLElement;
    const confirmElement = container?.querySelector('.form-success') as HTMLElement;

    if (!emailInput || !submitButton || !errorElement || !confirmElement) {
        console.error("Could not initialize forgot password form: missing elements");
        return;
    }

    emailInput.value = "";

    forgotPasswordForm.addEventListener('submit', async (event: Event) => {
        event.preventDefault();
        submitButton.disabled = true;
        
        const email = emailInput.value.trim() || "";
        if (!checkValidEmail(email)) {
            showError("L'adresse e-mail n'est pas valide");
            return;
        }

        try {
            const result = await fetch('/api/account/forgot-password', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email })
            });
            
            if (result.ok) {
                showSuccess();
            } else {
                const errorData = await result.json();
                throw new Error(errorData.error || 'Unknown error from server');
            }
        } catch (error) {
            console.error('Forgot password error:', error);
            showError("Une erreur est survenue. Veuillez réessayer plus tard.");
        } finally {
            submitButton.disabled = false;
        }
    });

    function showSuccess() {
        if (confirmElement) confirmElement.style.display = 'block';
        forgotPasswordForm.style.display = 'none';
    }

    function showError(message: string) {
        if (errorElement) {
            errorElement.textContent = message;
            errorElement.style.display = 'block';
            submitButton.disabled = false;
        }
    }
}


//* MARK: Reset password form
// ----------------------------------------------------------------------------

const resetPasswordForm = document.getElementById('reset-password-form') as HTMLFormElement | null;
if (resetPasswordForm) initResetPasswordForm(resetPasswordForm);

function initResetPasswordForm(resetPasswordForm: HTMLFormElement) {
    const passwordInput = resetPasswordForm.querySelector('input[name="password"]') as HTMLInputElement;
    const confirmPasswordInput = resetPasswordForm.querySelector('input[name="confirm-password"]') as HTMLInputElement;
    const submitButton = resetPasswordForm.querySelector('button[type="submit"]') as HTMLButtonElement;
    const errorElement = resetPasswordForm.querySelector('.error-message') as HTMLElement;
    const container = resetPasswordForm.closest('.center-form-container') as HTMLElement;
    const successElement = container?.querySelector('.form-success') as HTMLElement;
    const formErrorElement = container?.querySelector('.form-error') as HTMLElement;

    if (!passwordInput || !confirmPasswordInput || !submitButton || !errorElement || !successElement || !formErrorElement) {
        console.error("Could not initialize reset password form: missing elements");
        return;
    }

    // Get token from URL parameters
    const urlParams = new URLSearchParams(window.location.search);
    const token = urlParams.get('token');

    if (!token) {
        showFormError();
        return;
    }

    passwordInput.value = "";
    confirmPasswordInput.value = "";

    resetPasswordForm.addEventListener('submit', async (event: Event) => {
        event.preventDefault();
        submitButton.disabled = true;
        
        const password = passwordInput.value || "";
        const confirmPassword = confirmPasswordInput.value || "";

        // Validate password
        const passwordCheck = checkValidPassword(password);
        if (!passwordCheck.valid) {
            showError(passwordCheck.reason || 'invalid_password');
            return;
        }

        // Validate password confirmation
        if (password !== confirmPassword) {
            showError('new_passwords_dont_match');
            return;
        }

        try {
            const result = await fetch('/api/account/reset-password', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ token, password })
            });
            
            if (result.ok) {
                showSuccess();
            } else {
                const errorData = await result.json();
                if (result.status === 400 || result.status === 401) {
                    // Invalid or expired token
                    showFormError();
                } else {
                    throw new Error(errorData.error || 'Unknown error from server');
                }
            }
        } catch (error) {
            console.error('Reset password error:', error);
            showError('internal_error');
        } finally {
            submitButton.disabled = false;
        }
    });

    function showSuccess() {
        if (successElement) successElement.style.display = 'block';
        resetPasswordForm.style.display = 'none';
    }

    function showFormError() {
        if (formErrorElement) formErrorElement.style.display = 'block';
        resetPasswordForm.style.display = 'none';
    }

    function showError(requestError: RequestError) {
        if (errorElement) {
            errorElement.textContent = getPrettyErrorMessage(requestError);
            errorElement.style.display = 'block';
            submitButton.disabled = false;
        }
    }
}


//* MARK: Email confirmation
// ----------------------------------------------------------------------------

// Check if we're on the email confirmation page and handle token validation
const emailConfirmationElements = {
    loading: document.getElementById('loading-state'),
    success: document.getElementById('confirmation-success'),
    error: document.getElementById('confirmation-error'),
    alreadyConfirmed: document.getElementById('already-confirmed')
};

if (emailConfirmationElements.loading) {
    initEmailConfirmation();
}

async function initEmailConfirmation() {
    const { loading, success, error, alreadyConfirmed } = emailConfirmationElements;
    
    if (!loading || !success || !error || !alreadyConfirmed) {
        console.error("Could not initialize email confirmation: missing elements");
        return;
    }

    // Get token from URL parameters
    const urlParams = new URLSearchParams(window.location.search);
    const token = urlParams.get('token');
    const userid = urlParams.get('userid');

    if (!token || !userid) {
        showConfirmationError();
        return;
    }

    try {
        const result = await fetch('/api/account/verify-email', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ token, userid })
        });
        
        if (result.ok) {
            showConfirmationSuccess();
        } else {
            const data = await result.json();
            const error = data?.error as EmailVerificationError || 'internal_error';
            if (error === 'already_verified') {
                showAlreadyConfirmed();
            } else {
                console.error('Email confirmation error:', error);
                showConfirmationError();
            }
        }
    } catch (error) {
        console.error('Email confirmation network error:', error);
        showConfirmationError();
    }

    function showConfirmationSuccess() {
        if (loading) loading.style.display = 'none';
        if (success) success.style.display = 'block';
        if (error) error.style.display = 'none';
        if (alreadyConfirmed) alreadyConfirmed.style.display = 'none';
    }

    function showConfirmationError() {
        if (loading) loading.style.display = 'none';
        if (success) success.style.display = 'none';
        if (error) error.style.display = 'block';
        if (alreadyConfirmed) alreadyConfirmed.style.display = 'none';
    }

    function showAlreadyConfirmed() {
        if (loading) loading.style.display = 'none';
        if (success) success.style.display = 'none';
        if (error) error.style.display = 'none';
        if (alreadyConfirmed) alreadyConfirmed.style.display = 'block';
    }
}


//* MARK: Profile Page
// ----------------------------------------------------------------------------

// Profile page logic
const profilePage = document.getElementById('profile-page');
if (profilePage) initProfilePage();

function initProfilePage() {
    const displayNameInput = document.getElementById('profile-display-name') as HTMLInputElement | null;
    const emailInput = document.getElementById('profile-email') as HTMLInputElement | null;
    const emailVerifiedBadge = document.getElementById('profile-email-verified') as HTMLElement | null;
    const emailNonVerified = document.getElementById('profile-email-nonverified') as HTMLElement | null;
    const sendVerificationBtn = document.getElementById('send-verification-code-btn') as HTMLElement | null;
    const sendVerificationMsg = document.getElementById('send-verification-code-message') as HTMLElement | null;
    const paidLevel1 = document.getElementById('profile-paid-level-1') as HTMLElement | null;
    const paidLevel2 = document.getElementById('profile-paid-level-2') as HTMLElement | null;
    const paidLevel3 = document.getElementById('profile-paid-level-3') as HTMLElement | null;
    const profileForm = document.getElementById('profile-form') as HTMLFormElement | null;
    const profileMessage = document.getElementById('profile-message') as HTMLElement | null;
    const changePasswordForm = document.getElementById('change-password-form') as HTMLFormElement | null;
    const changePwdMessage = document.getElementById('change-password-message') as HTMLElement | null;

    const query = new URLSearchParams(window.location.search);
    if (query.get('profile-updated') === 'true' && profileMessage) {
        profileMessage.textContent = 'Profil mis à jour avec succès !';
        profileMessage.style.display = 'block';
        profileMessage.classList.remove('error');
        profileMessage.classList.add('success');
        window.setTimeout(() => {
            profileMessage.style.display = 'none';
            window.history.replaceState({}, document.title, window.location.pathname);
        }, 2500);
    }

    // Prefill user data
    fetch('/api/current-user-info').then(r => r.json()).then(data => {
        if (data.isAuthenticated && data.user) {
            const user = data.user;
            if (displayNameInput) displayNameInput.value = user.name || '';
            if (emailInput) emailInput.value = user.email || '';

            // Show correct verify state
            if (emailVerifiedBadge) emailVerifiedBadge.style.display = user.emailVerified ? 'block' : 'none';
            if (emailNonVerified) emailNonVerified.style.display = user.emailVerified ? 'none' : 'block';
            if (sendVerificationMsg) sendVerificationMsg.style.display = 'none';
            
            // Show current paid level
            switch (user.paidTier || 0) {
                case 1: if (paidLevel2) paidLevel2.style.display = 'block'; break;
                case 2: if (paidLevel3) paidLevel3.style.display = 'block'; break;
                default: if (paidLevel1) paidLevel1.style.display = 'block'; break;
            }
        } else {
            // Not authenticated - redirect to signin
            window.location.href = '/auth-signin?next=/profile';
        }
    }).catch(err => {
        console.error('Could not fetch user info', err);
    });

    if (profileForm) {
        function showProfileMessage(message: string, isError: boolean) {
            if (profileMessage) {
                profileMessage.textContent = message;
                profileMessage.style.display = 'block';
                profileMessage.classList.toggle('error', isError);
                profileMessage.classList.toggle('success', !isError);
            }
        }

        profileForm.addEventListener('submit', async (e) => {
            e.preventDefault();
            if (!displayNameInput || !emailInput) return;
            const name = displayNameInput.value.trim();
            const email = emailInput.value.trim();
            const submitBtn = profileForm.querySelector('button[type="submit"]') as HTMLButtonElement | null;
            if (submitBtn) submitBtn.disabled = true;
            try {
                const res = await fetch('/api/account/profile/update-infos', {
                    method: 'POST', headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ name, email })
                });
                if (res.ok) {
                    window.location.href = '/profile?profile-updated=true';
                } else {
                    const data = await res.json();
                    if (submitBtn) submitBtn.disabled = false;
                    const error = getPrettyErrorMessage(data.error as UpdateProfileError || 'internal_error');
                    showProfileMessage(error, true);
                }
            } catch (err) {
                console.error('Error updating profile', err);
                if (submitBtn) submitBtn.disabled = false;
                showProfileMessage('Une erreur est survenue. Veuillez réessayer plus tard.', true);
            }
        });
    }

    if (changePasswordForm) {
        function showPwdChangeResultMessage(message: string, isError: boolean) {
            if (changePwdMessage) {
                changePwdMessage.textContent = message;
                changePwdMessage.style.display = 'block';
                changePwdMessage.classList.toggle('error', isError);
                changePwdMessage.classList.toggle('success', !isError);
            }
        }

        changePasswordForm.addEventListener('submit', async (e) => {
            e.preventDefault();
            const currentInput = changePasswordForm.querySelector('input[name="current-password"]') as HTMLInputElement | null;
            const newInput = changePasswordForm.querySelector('input[name="new-password"]') as HTMLInputElement | null;
            const confirmInput = changePasswordForm.querySelector('input[name="confirm-new-password"]') as HTMLInputElement | null;
            const submitBtn = changePasswordForm.querySelector('button[type="submit"]') as HTMLButtonElement | null;
            if (!currentInput || !newInput || !confirmInput) return;
            const currentPwd = currentInput.value || '';
            const newPwd = newInput.value || '';
            const confirmPwd = confirmInput.value || '';
            if (newPwd !== confirmPwd) {
                showPwdChangeResultMessage('Les nouveaux mots de passe ne correspondent pas.', true);
                return;
            }
            if (submitBtn) submitBtn.disabled = true;
            try {
                const res = await fetch('/api/account/profile/update-password', {
                    method: 'POST', headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ currentPassword: currentPwd, newPassword: newPwd })
                });
                if (res.ok) {
                    showPwdChangeResultMessage('Mot de passe changé avec succès.', false);
                    window.setTimeout(() => { if (changePwdMessage) changePwdMessage.style.display = 'none'; }, 2500);
                    changePasswordForm.reset();
                } else {
                    const data = await res.json();
                    const errorMsg = getPrettyErrorMessage(data.error as RequestError || 'internal_error');
                    showPwdChangeResultMessage(errorMsg, true);
                }
            } catch (err) {
                console.error('Error changing password', err);
                showPwdChangeResultMessage('Une erreur est survenue. Veuillez réessayer plus tard.', true);
            } finally {
                if (submitBtn) submitBtn.disabled = false;
            }
        });
    }

    // Resend verification button
    if (sendVerificationBtn) {
        sendVerificationBtn.addEventListener('click', async (e) => {
            e.preventDefault();
            if (sendVerificationMsg) {
                sendVerificationMsg.textContent = 'Envoi en cours...';
                sendVerificationMsg.style.display = 'inline-block';
            }
            try {
                const res = await fetch('/api/account/profile/resend-verification', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({}) });
                if (res.ok) {
                    if (sendVerificationMsg) sendVerificationMsg.textContent = '📬️ L\'email de vérification a été envoyé.';
                    if (sendVerificationBtn) sendVerificationBtn.style.display = 'none';
                } else {
                    const data = await res.json();
                    if (sendVerificationMsg) {
                        const error: RequestError = data.error || 'internal_error';
                        sendVerificationMsg.textContent = getPrettyErrorMessage(error);
                    } 
                }
            } catch (err) {
                console.error('Error resending verification email', err);
                if (sendVerificationMsg) sendVerificationMsg.textContent = 'Une erreur est survenue. Veuillez réessayer plus tard.';
            }
        });
    }

    const deleteAccountBtn = document.getElementById('delete-account-btn') as HTMLButtonElement | null;
    const deleteAccountConfirmInput = document.getElementById('delete-account-confirm-input') as HTMLInputElement | null;
    if (deleteAccountBtn && deleteAccountConfirmInput) {
        deleteAccountBtn.disabled = true;
        deleteAccountConfirmInput.value = '';
        deleteAccountConfirmInput.addEventListener('input', () => {
            const confirmationText = deleteAccountConfirmInput.value.trim();
            deleteAccountBtn.disabled = confirmationText !== 'SUPPRIMER MON COMPTE';
        });

        deleteAccountBtn.addEventListener('click', async (e) => {
            e.preventDefault();
            const confirmationText = deleteAccountConfirmInput.value.trim();
            if (confirmationText !== 'SUPPRIMER MON COMPTE') return;
            if (!confirm('Êtes-vous sûr de vouloir supprimer votre compte ? Cette action est irréversible.')) {
                return;
            }
            deleteAccountBtn.disabled = true;
            try {
                const res = await fetch(
                    '/api/account/profile/delete-account', 
                    { method: 'POST', headers: { 'Content-Type': 'application/json' }, 
                    body: JSON.stringify({}) 
                });
                if (res.ok) {
                    alert('Votre compte a été supprimé avec succès.');
                    window.location.href = '/';
                } else {
                    const data = await res.json();
                    const errorMsg = getPrettyErrorMessage(data.error as RequestError || 'internal_error');
                    alert('Erreur lors de la suppression du compte : ' + errorMsg);
                    deleteAccountBtn.disabled = false;
                }
            } catch (err) {
                console.error('Error deleting account', err);
                alert('Une erreur est survenue. Veuillez réessayer plus tard.');
                deleteAccountBtn.disabled = false;
            }
        });
    }
}

//* MARK: Partners Scroll
// ----------------------------------------------------------------------------

const partnersScrollContainer = document.getElementById('partners-scroll-container') as HTMLElement | null;
const partnersScrollContent = document.getElementById('partners-scroll-content') as HTMLElement | null;
if (partnersScrollContainer && partnersScrollContent) {
    // Wait for all images to load before initializing scroll
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => {
            waitForImagesAndInitScroll(partnersScrollContainer, partnersScrollContent);
        });
    } else {
        waitForImagesAndInitScroll(partnersScrollContainer, partnersScrollContent);
    }
}

async function waitForImagesAndInitScroll(container: HTMLElement, content: HTMLElement) {
    const images = content.querySelectorAll('img');
    const imagePromises = Array.from(images).map(img => {
        if (img.complete) {
            return Promise.resolve();
        }
        return new Promise<void>((resolve) => {
            img.addEventListener('load', () => resolve());
            img.addEventListener('error', () => resolve()); // Resolve even on error to avoid hanging
        });
    });

    // Wait for all images to load, or timeout after 5 seconds
    await Promise.race([
        Promise.all(imagePromises),
        new Promise(resolve => setTimeout(resolve, 5000))
    ]);

    initPartnersScroll(container, content);
}

function initPartnersScroll(partnersScrollContainer: HTMLElement, partnersScrollContent: HTMLElement) {
    const containerWidth = partnersScrollContainer.offsetWidth;
    const contentWidth = partnersScrollContent.scrollWidth;

    if (contentWidth > (containerWidth - 120)) {
        // Duplicate partners for infinite scroll
        const currentLogos = partnersScrollContent.innerHTML;
        partnersScrollContent.innerHTML += currentLogos;
        partnersScrollContent.innerHTML += currentLogos;
        partnersScrollContent.innerHTML += currentLogos;
        partnersScrollContent.innerHTML += currentLogos;
        partnersScrollContent.innerHTML += currentLogos;
        partnersScrollContent.innerHTML += currentLogos;
        partnersScrollContent.innerHTML += currentLogos;

        // Apply scrolling animation via CSS
        const scrollDuration = (contentWidth / 100) * 8;
        partnersScrollContent.style.animation = `partners-scroll ${scrollDuration}s linear infinite`;
    }
}


//* MARK: Rel Keep Params
// ----------------------------------------------------------------------------

// Keep URL parameters for all links with rel="keep-params"
const keepParamsLinks = document.querySelectorAll('a[rel="keep-params"]') as NodeListOf<HTMLAnchorElement>;
if (keepParamsLinks.length > 0) {
    const currentParams = window.location.search;
    keepParamsLinks.forEach(link => {
        if (link.href) {
            const url = new URL(link.href);
            url.search += currentParams;
            link.href = url.toString();
        }
    });
}
const keepProductLinks = document.querySelectorAll('a[rel="keep-product"]') as NodeListOf<HTMLAnchorElement>;
if (keepProductLinks.length > 0) {
    const urlParams = new URLSearchParams(window.location.search);
    const product = urlParams.get('product');
    if (product === 'standard' || product === 'premium') {
        keepProductLinks.forEach(link => {
            if (link.href) {
                const url = new URL(link.href);
                url.searchParams.set('product', product);
                link.href = url.toString();
            }
        });
    }
}